/**
 * WeaveIn.ai Platform - Employer Plans Manager
 * Manages self-insured employer custom plan definitions and benefits
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Building,
  Plus,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Shield,
  Heart,
  Check,
  X,
  AlertCircle,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  healthPlansAPI,
  EmployerPlanDefinition,
  EmployerPlanCreate,
  BenefitCreate,
  Network,
  PRODUCT_TYPES,
  COVERAGE_TIERS,
  BENEFIT_CATEGORIES,
  ProductType,
  BenefitCategory,
} from '@/services/api/healthPlans';

// ============================================================================
// Types
// ============================================================================

interface EmployerPlansManagerProps {
  employerId: number;
  employerName?: string;
}

// ============================================================================
// Benefit Form
// ============================================================================

interface BenefitFormProps {
  onSave: (benefit: BenefitCreate) => void;
  onCancel: () => void;
}

const BenefitForm: React.FC<BenefitFormProps> = ({ onSave, onCancel }) => {
  const { t } = useAppTranslation();
  const [formData, setFormData] = useState<BenefitCreate>({
    benefit_category: 'PRIMARY_CARE',
    benefit_name: '',
    covered: true,
    in_network_deductible_applies: true,
    out_network_deductible_applies: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.benefit_name.trim()) return;
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <select
          value={formData.benefit_category}
          onChange={(e) => setFormData({ ...formData, benefit_category: e.target.value as BenefitCategory })}
          className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
        >
          {BENEFIT_CATEGORIES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
        </select>
        <input
          type="text"
          value={formData.benefit_name}
          onChange={(e) => setFormData({ ...formData, benefit_name: e.target.value })}
          placeholder={t('healthPlans.benefitForm.benefitNamePlaceholder', 'Benefit Name')}
          className="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          required
        />
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="covered"
            checked={formData.covered}
            onChange={(e) => setFormData({ ...formData, covered: e.target.checked })}
            className="h-4 w-4 text-blue-600 rounded"
          />
          <label htmlFor="covered" className="text-sm dark:text-gray-300">{t('healthPlans.benefitForm.covered', 'Covered')}</label>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('healthPlans.benefitForm.inNetworkCopay', 'In-Network Copay')}</label>
          <input
            type="number"
            value={formData.in_network_copay || ''}
            onChange={(e) => setFormData({ ...formData, in_network_copay: parseFloat(e.target.value) || undefined })}
            placeholder="$0"
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('healthPlans.benefitForm.inNetworkCoinsurance', 'In-Network Coinsurance %')}</label>
          <input
            type="number"
            value={formData.in_network_coinsurance_pct || ''}
            onChange={(e) => setFormData({ ...formData, in_network_coinsurance_pct: parseFloat(e.target.value) || undefined })}
            placeholder="0%"
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('healthPlans.benefitForm.outNetworkCopay', 'Out-Network Copay')}</label>
          <input
            type="number"
            value={formData.out_network_copay || ''}
            onChange={(e) => setFormData({ ...formData, out_network_copay: parseFloat(e.target.value) || undefined })}
            placeholder="$0"
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">{t('healthPlans.benefitForm.outNetworkCoinsurance', 'Out-Network Coinsurance %')}</label>
          <input
            type="number"
            value={formData.out_network_coinsurance_pct || ''}
            onChange={(e) => setFormData({ ...formData, out_network_coinsurance_pct: parseFloat(e.target.value) || undefined })}
            placeholder="0%"
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="requires_auth"
            checked={formData.requires_authorization || false}
            onChange={(e) => setFormData({ ...formData, requires_authorization: e.target.checked })}
            className="h-4 w-4 text-blue-600 rounded"
          />
          <label htmlFor="requires_auth" className="text-xs dark:text-gray-300">{t('healthPlans.benefitForm.requiresAuth', 'Requires Auth')}</label>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="requires_referral"
            checked={formData.requires_referral || false}
            onChange={(e) => setFormData({ ...formData, requires_referral: e.target.checked })}
            className="h-4 w-4 text-blue-600 rounded"
          />
          <label htmlFor="requires_referral" className="text-xs dark:text-gray-300">{t('healthPlans.benefitForm.requiresReferral', 'Requires Referral')}</label>
        </div>
        <div>
          <input
            type="number"
            value={formData.annual_limit || ''}
            onChange={(e) => setFormData({ ...formData, annual_limit: parseInt(e.target.value) || undefined })}
            placeholder={t('healthPlans.benefitForm.annualLimitPlaceholder', 'Annual Limit')}
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            value={formData.limit_unit || ''}
            onChange={(e) => setFormData({ ...formData, limit_unit: e.target.value })}
            placeholder={t('healthPlans.benefitForm.limitUnitPlaceholder', 'Limit Unit (visits, days)')}
            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">{t('healthPlans.benefitForm.addBenefit', 'Add Benefit')}</button>
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-gray-600 dark:text-gray-300">{t('healthPlans.common.cancel', 'Cancel')}</button>
      </div>
    </form>
  );
};

// ============================================================================
// Plan Card
// ============================================================================

interface PlanCardProps {
  plan: EmployerPlanDefinition;
  onEdit: () => void;
  onDelete: () => void;
  expanded: boolean;
  onToggle: () => void;
}

const PlanCard: React.FC<PlanCardProps> = ({ plan, onEdit, onDelete, expanded, onToggle }) => {
  const { t } = useAppTranslation();
  const formatCurrency = (amount?: number) => amount ? `$${amount.toLocaleString()}` : '-';

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white">{plan.name}</h4>
            <p className="text-sm text-gray-500">{plan.plan_code}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
            {PRODUCT_TYPES.find(p => p.value === plan.plan_type)?.label}
          </span>
          {plan.coverage_tier && (
            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-full">
              {COVERAGE_TIERS.find(c => c.value === plan.coverage_tier)?.label}
            </span>
          )}
          {plan.hsa_eligible && (
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
              HSA
            </span>
          )}
          <span className={`px-2 py-0.5 text-xs rounded-full ${
            plan.status === 'ACTIVE' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
            'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {plan.status}
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Financial Summary */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.employeePremium', 'Employee Premium')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.employee_premium)}/mo</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.employerPremium', 'Employer Premium')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.employer_premium)}/mo</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.individualDeductible', 'Individual Deductible')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.deductible_individual)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.familyDeductible', 'Family Deductible')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.deductible_family)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.individualOOPMax', 'Individual OOP Max')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.out_of_pocket_max_individual)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('healthPlans.planCard.familyOOPMax', 'Family OOP Max')}</p>
                <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(plan.out_of_pocket_max_family)}</p>
              </div>
            </div>

            {plan.hsa_eligible && plan.hsa_employer_contribution && (
              <div className="mt-3 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg text-sm">
                <span className="font-medium text-green-700 dark:text-green-300">{t('healthPlans.planCard.hsaEmployerContribution', 'HSA Employer Contribution')}:</span>
                <span className="ml-2 text-green-600 dark:text-green-400">{formatCurrency(plan.hsa_employer_contribution)}/{t('healthPlans.common.year', 'year')}</span>
              </div>
            )}

            {plan.network_name && (
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium">{t('healthPlans.planCard.network', 'Network')}:</span> {plan.network_name}
              </div>
            )}
          </div>

          {/* Benefits */}
          {plan.benefits && plan.benefits.length > 0 && (
            <div className="p-4">
              <h5 className="font-medium text-sm text-gray-900 dark:text-white mb-3">{t('healthPlans.planCard.benefits', 'Benefits')} ({plan.benefits.length})</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {plan.benefits.map(benefit => (
                  <div key={benefit.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm">
                    <div className="flex items-center gap-2">
                      {benefit.covered ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <X className="w-4 h-4 text-red-600" />
                      )}
                      <span className="text-gray-900 dark:text-white">{benefit.benefit_name}</span>
                      {benefit.requires_authorization && (
                        <span className="text-xs text-orange-600">{t('healthPlans.planCard.authReq', 'Auth Req')}</span>
                      )}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 text-xs">
                      {benefit.in_network_copay ? `$${benefit.in_network_copay}` : ''}
                      {benefit.in_network_coinsurance_pct ? `${benefit.in_network_coinsurance_pct}%` : ''}
                      {!benefit.in_network_copay && !benefit.in_network_coinsurance_pct && benefit.covered ? t('healthPlans.benefitForm.covered', 'Covered') : ''}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={onEdit}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
            >
              <Edit className="w-4 h-4" /> {t('healthPlans.planCard.editPlan', 'Edit Plan')}
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
            >
              <Trash2 className="w-4 h-4" /> {t('healthPlans.common.delete', 'Delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export default function EmployerPlansManager({ employerId, employerName }: EmployerPlansManagerProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const [plans, setPlans] = useState<EmployerPlanDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);
  const [showBenefitForm, setShowBenefitForm] = useState<number | null>(null);
  const [formData, setFormData] = useState<EmployerPlanCreate>({
    plan_code: '',
    name: '',
    plan_type: 'PPO',
  });
  const [saving, setSaving] = useState(false);

  // Load employer plans
  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await healthPlansAPI.listEmployerPlans(employerId);
      setPlans(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, [employerId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.plan_code.trim() || !formData.name.trim()) {
      setError(t('healthPlans.errors.planCodeNameRequired', 'Plan code and name are required'));
      return;
    }

    setSaving(true);
    try {
      await healthPlansAPI.createEmployerPlan(employerId, formData);
      setShowForm(false);
      setFormData({ plan_code: '', name: '', plan_type: 'PPO' });
      loadPlans();
    } catch (err: any) {
      setError(err.message || 'Failed to create plan');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (planId: number) => {
    if (!window.confirm(t('healthPlans.confirmDeletePlan', 'Are you sure you want to delete this plan?'))) return;
    // Note: Delete endpoint would need to be implemented
    console.log('Delete plan:', planId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          {!tabbed && (
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6" />
            {t('healthPlans.employerPlans.title', 'Self-Insured Plan Definitions')}
          </h2>
          )}
          {employerName && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{employerName}</p>
          )}
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          {t('healthPlans.employerPlans.addPlan', 'Add Plan')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto underline text-sm">{t('healthPlans.common.dismiss', 'Dismiss')}</button>
        </div>
      )}

      {/* Create Plan Form */}
      {showForm && (
        <div className="p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">{t('healthPlans.employerPlans.createNewPlan', 'Create New Plan')}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.planCode', 'Plan Code')} *</label>
                <input
                  type="text"
                  value={formData.plan_code}
                  onChange={(e) => setFormData({ ...formData, plan_code: e.target.value })}
                  placeholder={t('healthPlans.form.planCodePlaceholder', 'e.g., PPO-2024')}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.planName', 'Plan Name')} *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('healthPlans.form.planNamePlaceholder', 'e.g., Gold PPO Plan')}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.planType', 'Plan Type')}</label>
                <select
                  value={formData.plan_type}
                  onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as ProductType })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {PRODUCT_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>

            {/* Row 2: Coverage Tier & HSA */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.coverageTier', 'Coverage Tier')}</label>
                <select
                  value={formData.coverage_tier || ''}
                  onChange={(e) => setFormData({ ...formData, coverage_tier: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">{t('healthPlans.common.select', 'Select...')}</option>
                  {COVERAGE_TIERS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-4 md:col-span-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="hsa_eligible"
                    checked={formData.hsa_eligible || false}
                    onChange={(e) => setFormData({ ...formData, hsa_eligible: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="hsa_eligible" className="text-sm text-gray-700 dark:text-gray-300">{t('healthPlans.form.hsaEligible', 'HSA Eligible')}</label>
                </div>
                {formData.hsa_eligible && (
                  <div className="flex-1">
                    <input
                      type="number"
                      value={formData.hsa_employer_contribution || ''}
                      onChange={(e) => setFormData({ ...formData, hsa_employer_contribution: parseFloat(e.target.value) || undefined })}
                      placeholder={t('healthPlans.form.employerHSAContribution', 'Employer HSA Contribution')}
                      className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.effectiveDate', 'Effective Date')}</label>
                <input
                  type="date"
                  value={formData.effective_date || ''}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            {/* Row 3: Premiums */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.employeePremiumMonthly', 'Employee Premium (Monthly)')}</label>
                <input
                  type="number"
                  value={formData.employee_premium || ''}
                  onChange={(e) => setFormData({ ...formData, employee_premium: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.employerPremiumMonthly', 'Employer Premium (Monthly)')}</label>
                <input
                  type="number"
                  value={formData.employer_premium || ''}
                  onChange={(e) => setFormData({ ...formData, employer_premium: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            {/* Row 4: Deductibles & OOP Max */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.individualDeductible', 'Individual Deductible')}</label>
                <input
                  type="number"
                  value={formData.deductible_individual || ''}
                  onChange={(e) => setFormData({ ...formData, deductible_individual: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.familyDeductible', 'Family Deductible')}</label>
                <input
                  type="number"
                  value={formData.deductible_family || ''}
                  onChange={(e) => setFormData({ ...formData, deductible_family: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.individualOOPMax', 'Individual OOP Max')}</label>
                <input
                  type="number"
                  value={formData.out_of_pocket_max_individual || ''}
                  onChange={(e) => setFormData({ ...formData, out_of_pocket_max_individual: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('healthPlans.form.familyOOPMax', 'Family OOP Max')}</label>
                <input
                  type="number"
                  value={formData.out_of_pocket_max_family || ''}
                  onChange={(e) => setFormData({ ...formData, out_of_pocket_max_family: parseFloat(e.target.value) || undefined })}
                  placeholder="$0.00"
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {saving ? t('healthPlans.common.creating', 'Creating...') : t('healthPlans.employerPlans.createPlan', 'Create Plan')}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {t('healthPlans.common.cancel', 'Cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Plans List */}
      {plans.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{t('healthPlans.employerPlans.noPlansYet', 'No plans defined yet')}</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{t('healthPlans.employerPlans.createFirstPlan', 'Create your first self-insured plan definition')}</p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 inline mr-2" />
            {t('healthPlans.employerPlans.createPlan', 'Create Plan')}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              expanded={expandedPlan === plan.id}
              onToggle={() => setExpandedPlan(expandedPlan === plan.id ? null : plan.id)}
              onEdit={() => console.log('Edit plan:', plan.id)}
              onDelete={() => handleDelete(plan.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
